import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { formatDate } from "@/utils/format";

const HOT_KEYWORDS = ["星海列车", "国产动画", "新番", "悬疑", "治愈", "剧场版"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [draft, setDraft] = useState(keyword);
  const [tab, setTab] = useState<"all" | "anime" | "news">("all");

  useEffect(() => {
    setDraft(keyword);
    setTab("all");
  }, [keyword]);

  const query = keyword.trim().toLowerCase();

  const matchedAnime = useMemo(() => {
    if (!query) return [];
    return animeList.filter((a) =>
      [a.title, a.jpTitle, a.summary, a.studio, a.region, ...a.genres, ...a.tags]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [query]);

  const matchedNews = useMemo(() => {
    if (!query) return [];
    return newsList.filter((n) =>
      [n.title, n.summary, n.author, n.category, ...n.tags].join(" ").toLowerCase().includes(query),
    );
  }, [query]);

  const total = matchedAnime.length + matchedNews.length;
  const showAnime = tab !== "news";
  const showNews = tab !== "anime";

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    setParams(trimmed ? { q: trimmed } : {}, { replace: true });
  };

  return (
    <SiteLayout
      title={keyword ? `${keyword} - 搜索结果 - 91动漫` : "搜索动漫资讯与作品 - 91动漫"}
      description="在 91动漫 搜索动漫作品与资讯，支持按名称、类型、标签检索，快速定位想看的动画与最新动态。"
      keywords="动漫搜索,动漫作品搜索,动漫资讯搜索,91动漫"
    >
      <section className="w-full bg-foreground-950">
        <div className="w-full px-4 md:px-8 pt-8 md:pt-10 pb-8 md:pb-12">
          <div className="text-background-300">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          </div>
          <h1 className="mt-4 font-heading font-bold text-background-50 text-2xl md:text-4xl tracking-tight">
            搜索动漫
          </h1>
          <p className="mt-2 text-sm text-background-300">输入作品名称、类型或资讯关键词，一次检索作品与资讯。</p>

          <form onSubmit={submit} className="mt-5 max-w-3xl flex flex-col sm:flex-row gap-2.5">
            <div className="flex-1 flex items-center gap-2.5 h-12 px-4 rounded-md bg-background-50">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="例如：星海列车、悬疑、国产动画"
                aria-label="搜索关键词"
                className="flex-1 bg-transparent outline-none text-sm md:text-base text-foreground-900 placeholder:text-foreground-400"
              />
              {draft && (
                <button
                  type="button"
                  onClick={() => setDraft("")}
                  aria-label="清空关键词"
                  className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-circle-fill text-base" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-12 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-background-400">热门搜索：</span>
            {HOT_KEYWORDS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setParams({ q: k }, { replace: true })}
                className="px-2.5 py-1 rounded-full bg-background-50/10 hover:bg-background-50/20 text-[11px] text-background-200 transition-colors duration-200 cursor-pointer"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50 min-h-[420px]">
        {!query ? (
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            desc="可以搜索动漫作品名称、动画类型、标签，或资讯标题中的关键词。例如「星海列车」「悬疑」「新番」。"
            actionLabel="浏览全部作品"
            actionTo="/anime"
          />
        ) : total === 0 ? (
          <div className="flex flex-col gap-6">
            <EmptyState
              icon="ri-emotion-sad-line"
              title={`未找到与「${keyword}」相关的内容`}
              desc="试试更短的关键词，或者从下面的热门分类开始浏览。当前搜索为前端演示检索，数据为站内模拟内容。"
              actionLabel="返回首页"
              actionTo="/"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/news"
                className="p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors duration-200 cursor-pointer"
              >
                <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-newspaper-line text-base" />
                </span>
                <h2 className="mt-3 font-heading font-semibold text-foreground-950 text-sm">去看动漫资讯</h2>
                <p className="mt-1 text-xs text-foreground-600 leading-relaxed">新番情报、行业动态与活动消息都在这里。</p>
              </Link>
              <Link
                to="/anime"
                className="p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors duration-200 cursor-pointer"
              >
                <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className="ri-book-2-line text-base" />
                </span>
                <h2 className="mt-3 font-heading font-semibold text-foreground-950 text-sm">去逛作品图鉴</h2>
                <p className="mt-1 text-xs text-foreground-600 leading-relaxed">按类型、地区与年份筛选，找到下一部想追的番。</p>
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <div>
                <p className="text-sm text-foreground-700">
                  关键词 <span className="font-medium text-primary-600">「{keyword}」</span> 共找到{" "}
                  <span className="font-heading font-bold text-foreground-950">{total}</span> 条结果
                </p>
                <p className="mt-1 text-[11px] text-foreground-400">前端演示检索结果，数据来自站内模拟内容</p>
              </div>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 self-start">
                {[
                  { key: "all" as const, label: `全部 ${total}` },
                  { key: "anime" as const, label: `作品 ${matchedAnime.length}` },
                  { key: "news" as const, label: `资讯 ${matchedNews.length}` },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-3.5 h-8 rounded-full text-xs whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                      tab === t.key ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
              <div className="min-w-0 flex flex-col gap-8">
                {showAnime && matchedAnime.length > 0 && (
                  <div>
                    <h2 className="font-heading font-bold text-foreground-950 text-base md:text-lg flex items-center gap-2 mb-4">
                      <span className="w-1.5 h-4 rounded-full bg-primary-500" />
                      动漫作品
                      <span className="text-xs font-normal text-foreground-400">（{matchedAnime.length}）</span>
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-5" data-product-shop>
                      {matchedAnime.map((a, idx) => (
                        <AnimeCard key={a.id} anime={a} index={idx} />
                      ))}
                    </div>
                  </div>
                )}

                {showNews && matchedNews.length > 0 && (
                  <div>
                    <h2 className="font-heading font-bold text-foreground-950 text-base md:text-lg flex items-center gap-2 mb-4">
                      <span className="w-1.5 h-4 rounded-full bg-accent-500" />
                      动漫资讯
                      <span className="text-xs font-normal text-foreground-400">（{matchedNews.length}）</span>
                    </h2>
                    <div className="flex flex-col gap-3">
                      {matchedNews.map((n) => (
                        <Link
                          key={n.id}
                          to={`/news/detail/${n.id}`}
                          className="group flex gap-3 md:gap-4 p-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors duration-300 cursor-pointer"
                        >
                          <div className="w-24 h-16 sm:w-32 sm:h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                            <img
                              src={n.cover}
                              alt={n.title}
                              title={`${n.title} 搜索结果`}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-900 text-[10px] font-label">
                              {n.category}
                            </span>
                            <h3 className="mt-1.5 text-sm md:text-base font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                              {n.title}
                            </h3>
                            <p className="mt-1 text-[11px] text-foreground-500 font-label hidden sm:block">
                              {formatDate(n.publishedAt)} · {n.author}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {((showAnime && matchedAnime.length === 0 && tab === "anime") ||
                  (showNews && matchedNews.length === 0 && tab === "news")) && (
                  <EmptyState
                    icon="ri-search-line"
                    title="该分类下没有结果"
                    desc="切换到「全部」看看另一类内容，或换一个关键词再试。"
                    actionLabel="浏览全部作品"
                    actionTo="/anime"
                  />
                )}
              </div>

              <aside className="flex flex-col gap-5">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading font-semibold text-foreground-950 text-sm">搜索建议</h2>
                  <ul className="mt-3 space-y-2 text-xs text-foreground-600 leading-relaxed">
                    <li className="flex gap-2">
                      <i className="ri-checkbox-blank-circle-line text-[8px] text-primary-500 mt-1.5" />
                      使用作品名称的一部分，例如「列车」
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-checkbox-blank-circle-line text-[8px] text-primary-500 mt-1.5" />
                      直接用类型词，如「悬疑」「治愈」「热血」
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-checkbox-blank-circle-line text-[8px] text-primary-500 mt-1.5" />
                      资讯标题中的关键人名或公司名也可以
                    </li>
                  </ul>
                </div>
                <div className="rounded-lg bg-foreground-950 p-5">
                  <h2 className="font-heading font-semibold text-background-50 text-sm">热门作品</h2>
                  <ul className="mt-3 space-y-2.5">
                    {[...animeList].sort((a, b) => b.heat - a.heat).slice(0, 5).map((a, idx) => (
                      <li key={a.id}>
                        <Link
                          to={`/anime/detail/${a.id}`}
                          className="flex items-center gap-2 text-xs text-background-200 hover:text-primary-400 transition-colors duration-200 cursor-pointer"
                        >
                          <span className="font-label text-background-500 w-4">{idx + 1}</span>
                          <span className="truncate">{a.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </>
        )}
      </section>
    </SiteLayout>
  );
}